/**
 * Species landing pages for the calculator: /dog-calorie-calculator/ and /cat-calorie-calculator/.
 *
 * Added 2026-09-30. The site had no page aimed at the generic searches ("dog calorie calculator",
 * "cat calorie calculator") while the home page is titled as a feeding calculator. The copy lives
 * here, not in App.tsx, so scripts/prerender.mjs can emit it in full for crawlers.
 *
 * Every number in this file is computed from src/utils/nutritionMath.ts at load time, so the page
 * cannot state a factor or a worked example that the calculator does not actually use.
 */
import { calculateMER, calculateMeasurements, calculateRER, MER_MULTIPLIERS, type Species } from "@/utils/nutritionMath";
import { catStatuses, dogStatuses } from "@/data/content";

export type CalculatorPageKey = "dog-calorie-calculator" | "cat-calorie-calculator";
export type CalculatorPageSection = { title: string; paragraphs: string[]; bullets?: string[] };
export type CalculatorSource = { label: string; url: string };
export type CalculatorFaq = { q: string; a: string };
export type CalculatorPage = {
  species: Species;
  h1: string;
  intro: string;
  sections: CalculatorPageSection[];
  faqs: CalculatorFaq[];
  related: string[];
  sources: CalculatorSource[];
};

const SOURCES: CalculatorSource[] = [
  {
    label: "AAHA 2021 Nutrition and Weight Management Guidelines: weight reduction in the obese pet",
    url: "https://www.aaha.org/resources/2021-aaha-nutrition-and-weight-management-guidelines/weight-reduction-in-the-obese-pet/",
  },
  { label: "Pet Nutrition Alliance calorie calculator", url: "https://petnutritionalliance.org/resources/calorie-calculator/" },
];

const fmt = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 1 });
const factor = (species: Species, status: string) => MER_MULTIPLIERS[species][status].toFixed(1);

// Weight loss is the one factor that applies to a different weight, so its bullet says so; a reader
// applying "1.0 x RER" to current weight makes exactly the mistake the page warns about.
const factorBullets = (species: Species) =>
  (species === "dog" ? dogStatuses : catStatuses).map(
    (s) => `${s.label}: ${factor(species, s.value)} x RER${s.value === "weight_loss" ? " at the target weight" : ""}`,
  );

// Example inputs live here once and feed both the maths and the sentences that describe them.
type Example = { kg: number; kcalPerKg: number; lossFromKg: number; lossToKg: number; growthKg: number };
const EXAMPLES: Record<Species, Example> = {
  dog: { kg: 20, kcalPerKg: 3600, lossFromKg: 40, lossToKg: 32, growthKg: 5 },
  cat: { kg: 4.5, kcalPerKg: 3800, lossFromKg: 6, lossToKg: 5, growthKg: 2 },
};

function numbers(species: Species) {
  const e = EXAMPLES[species];
  const daily = calculateMER(species, "neutered_adult", e.kg);
  const growthStatus = species === "dog" ? "puppy_under_4m" : "kitten";
  return {
    ...e,
    maint: factor(species, "neutered_adult"),
    rer: fmt(Math.round(calculateRER(e.kg))),
    daily: fmt(daily),
    grams: fmt(calculateMeasurements(daily, e.kcalPerKg).gramsPerDay),
    lossCurrent: fmt(calculateMER(species, "weight_loss", e.lossFromKg)),
    lossTarget: fmt(calculateMER(species, "weight_loss", e.lossFromKg, e.lossToKg)),
    growthDaily: fmt(calculateMER(species, growthStatus, e.growthKg)),
    growthFactor: factor(species, growthStatus),
  };
}

function explainer(species: Species) {
  const n = numbers(species);
  const pet = species;
  return {
    formula: [
      `The starting point is the resting energy requirement, or RER: the calories a ${pet}'s body needs in a day just to keep running while lying still, before any ${species === "dog" ? "walking or play" : "play or hunting"}. The formula is RER = 70 x body weight in kg raised to the power 0.75, from the 2021 AAHA Nutrition and Weight Management Guidelines. You don't need to work it out yourself; the calculator does it.`,
      `RER is then multiplied by a factor for your ${pet}'s life stage or goal. "${n.maint} x RER" means ${n.maint} times the resting figure. Where AAHA gives a range for a life stage, the calculator uses a single value from inside it. These are the values it uses:`,
    ],
    example: `A ${fmt(n.kg)} kg neutered adult ${pet} has an RER of about ${n.rer} kcal. At ${n.maint} x RER that is about ${n.daily} kcal a day. On a dry food with ${fmt(n.kcalPerKg)} kcal/kg, that works out to about ${n.grams} g a day.`,
    density: `The daily amount comes from dividing daily calories by the food's calorie density, meaning how many calories are in a kilogram or a cup of it. US labels carry a Calorie Content statement in kcal/kg and often kcal per ${species === "dog" ? "cup" : "cup or per can"}; EU and UK labels usually give kcal/kg or kcal/100g. The label guide shows where to find these numbers.`,
    lossRule: `For weight loss, the AAHA guidelines base the calculation on the ${pet}'s ideal weight, not its current weight, and the Pet Nutrition Alliance calculator does the same. Enter the target weight your vet gave you. If you leave it blank, the calculator uses current weight, and the result will be higher than your vet's plan would give.`,
    lossExample: `Take a ${fmt(n.lossFromKg)} kg ${pet} with a ${fmt(n.lossToKg)} kg target. Using the weight-loss factor on the target weight gives about ${n.lossTarget} kcal a day. The same factor on its current ${fmt(n.lossFromKg)} kg would give about ${n.lossCurrent}.`,
    vet: `The result is a starting point for a healthy ${pet}, not a diagnosis. Ask your vet before relying on it if your ${pet} is pregnant or nursing, very overweight or underweight, diabetic, has kidney or heart disease, or is recovering from illness. Weigh your ${pet} and check its body condition every two to four weeks, and adjust the amount gradually. ${species === "dog" ? "Treats and chews count" : "Treats count"} toward the daily total.`,
  };
}

const dog = explainer("dog");
const cat = explainer("cat");
const dn = numbers("dog");
const cn = numbers("cat");

export const calculatorPages: Record<CalculatorPageKey, CalculatorPage> = {
  "dog-calorie-calculator": {
    species: "dog",
    h1: "Dog Calorie Calculator",
    intro:
      "Enter your dog's weight, life stage or goal, and the calories printed on the food label. The calculator gives a daily calorie estimate and turns it into grams, and cups if you want them, for the food you actually feed, puppies included.",
    sections: [
      { title: "How the daily calorie estimate is worked out", paragraphs: dog.formula, bullets: factorBullets("dog") },
      {
        title: "Worked example",
        paragraphs: [
          dog.example,
          "A food with a different calorie density gives a different amount in grams for the same dog, which is why the calculator asks for the number on your label rather than assuming one.",
        ],
      },
      { title: "Turning calories into grams or cups", paragraphs: [dog.density] },
      { title: "Weight loss: use the target weight", paragraphs: [dog.lossRule, dog.lossExample] },
      { title: "When to ask a vet instead", paragraphs: [dog.vet] },
    ],
    faqs: [
      {
        q: "How many calories does my dog need a day?",
        a: `It depends on your dog's weight and life stage. A ${fmt(dn.kg)} kg neutered adult dog needs about ${dn.daily} kcal a day. The calculator works out an estimate for any weight.`,
      },
      {
        q: "How many calories does a puppy need?",
        a: `Puppies under 4 months use ${dn.growthFactor} x RER, while puppies over 4 months use ${factor("dog", "puppy_over_4m")} x RER. For a ${fmt(dn.growthKg)} kg puppy, that is about ${dn.growthDaily} kcal a day under 4 months and ${fmt(calculateMER("dog", "puppy_over_4m", dn.growthKg))} kcal over 4 months. Needs change quickly during growth, so re-weigh and recalculate regularly.`,
      },
      {
        q: "How do I turn daily calories into grams or cups of food?",
        a: `Divide daily calories by the calorie density on the label, using kcal/kg for grams or kcal per cup for cups. For the ${fmt(dn.kg)} kg neutered adult dog eating ${dn.daily} kcal a day, food with ${fmt(dn.kcalPerKg)} kcal/kg works out to about ${dn.grams} g a day. The calculator does this for you, in grams and, if you want them, cups.`,
      },
      {
        q: "How many calories should a dog eat to lose weight?",
        a: `Following the AAHA 2021 guidelines, the calculator applies the weight-loss factor to the target weight. For a ${fmt(dn.lossFromKg)} kg dog with a ${fmt(dn.lossToKg)} kg target, that gives about ${dn.lossTarget} kcal a day. Agree the target weight with your vet.`,
      },
      {
        q: "Is a kcal on a pet food label the same as a calorie?",
        a: "Yes, pet food labels use kilocalories (kcal), which are the same unit as the Calories on human food labels. The calculator works in kcal throughout.",
      },
    ],
    sources: SOURCES,
    related: ["overfeeding-risks", "why-pet-weight-loss-attempts-fail", "how-to-read-calorie-content-pet-food-label"],
  },
  "cat-calorie-calculator": {
    species: "cat",
    h1: "Cat Calorie Calculator",
    intro:
      "Enter your cat's weight, life stage or goal, and the calories printed on the food label. The calculator gives a daily calorie estimate and turns it into grams, and cups if you want them, for the dry or wet food you actually feed, including a split between the two.",
    sections: [
      { title: "How the daily calorie estimate is worked out", paragraphs: cat.formula, bullets: factorBullets("cat") },
      {
        title: "Worked example",
        paragraphs: [
          cat.example,
          "Wet food carries far fewer calories per gram than dry food, so the same cat needs many more grams of it. If you feed both, the calculator can split the day between two foods.",
        ],
      },
      { title: "Turning calories into grams or cups", paragraphs: [cat.density] },
      {
        title: "Weight loss: use the target weight, and involve a vet",
        paragraphs: [
          cat.lossRule,
          cat.lossExample,
          "Cats should not go on a sharp calorie cut at home. A cat that stops eating enough can develop hepatic lipidosis, a serious liver condition, so a weight-loss plan for a cat belongs with a veterinarian.",
        ],
      },
      { title: "When to ask a vet instead", paragraphs: [cat.vet] },
    ],
    faqs: [
      {
        q: "How many calories does a cat need a day?",
        a: `It depends on your cat's weight and life stage. A ${fmt(cn.kg)} kg neutered adult cat needs about ${cn.daily} kcal a day. Enter your cat's weight in the calculator for its daily estimate.`,
      },
      {
        q: "How many calories does a kitten need?",
        a: `The calculator uses ${cn.growthFactor} x RER for kittens. A ${fmt(cn.growthKg)} kg kitten needs about ${cn.growthDaily} kcal a day. Re-weigh and recalculate as your kitten grows.`,
      },
      {
        q: "How much dry food should I feed my cat?",
        a: `It depends on the food's calorie density, so there is no single gram figure for every food. A ${fmt(cn.kg)} kg neutered adult cat eating ${cn.daily} kcal a day needs about ${cn.grams} g of dry food with ${fmt(cn.kcalPerKg)} kcal/kg. Use the calorie density on your own label in the calculator.`,
      },
      {
        q: "How many calories should a cat eat to lose weight?",
        a: `The calculator applies the weight-loss factor to the target weight. For a ${fmt(cn.lossFromKg)} kg cat with a ${fmt(cn.lossToKg)} kg target, that gives about ${cn.lossTarget} kcal a day. A cat's weight-loss plan belongs with a veterinarian because a cat that stops eating enough can develop hepatic lipidosis, a serious liver condition.`,
      },
      {
        q: "Can I feed my cat both wet and dry food?",
        a: "Yes, the calculator can split daily calories between two foods. Wet food has far fewer calories per gram than dry food, so the gram amounts differ a lot.",
      },
    ],
    sources: SOURCES,
    related: ["indoor-cat-obesity-risk-first-birthday", "diabetic-cats-weight-loss-remission", "how-to-read-calorie-content-pet-food-label"],
  },
};

export const calculatorPageKeys = Object.keys(calculatorPages) as CalculatorPageKey[];
